import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserMinusIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon,
  ArchiveBoxIcon,
  UserCircleIcon,
  CalculatorIcon,
  DocumentTextIcon,
  ComputerDesktopIcon,
  CloudIcon,
  WrenchScrewdriverIcon,
  ArrowDownTrayIcon,
  HomeModernIcon,
  BriefcaseIcon,
  UsersIcon,
  AcademicCapIcon,
  BookOpenIcon,
  WalletIcon,
  ArrowLongRightIcon,
  ClockIcon as ClockOutline,
  DocumentArrowDownIcon,
  TableCellsIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckSolid, MagnifyingGlassIcon, XCircleIcon as XSolid, ClockIcon, BuildingOfficeIcon } from '@heroicons/react/24/solid';
import { adminOffboardingAPI } from '../services/employee.service';

// --- Configuration and Styling ---
const PRIMARY_COLOR_CLASS = 'red'; // Theme color: red for HRMS systems
const NOTICE_PERIOD_DAYS = 60; // Standard company notice period
const DEFAULT_TEST_ID = 'EMP20260080'; // ID pre-filled for easy testing

// --- Mock Data Store ---

const mockEmployeeDB = {
    'EMP-1234': {
        name: 'Vivek Chandra',
        title: 'Principal Data Scientist',
        department: 'Advanced Analytics',
        joinDate: '2022-03-15',
        manager: 'Deepa Narayan',
        officialEmail: 'vivek.c@acmeco.com',
        // EMP-1234 requires role-specific assets like Office Keys
        currentAssets: ['Laptop (ACME-LPT-901)', 'Mobile (ACME-MB-22)', 'ID Card', 'Office Keys', 'Monitor 27"'],
        projectStatus: '90% Handover Complete (Pending: Documentation)',
        trainingStatus: 'All mandatory exit training sessions completed.',
        pendingLeaves: 15,
        employeeStatus: 'Active', 
    },
    'EMP-5678': {
        name: 'Sara Khan',
        title: 'Marketing Specialist',
        department: 'Digital Marketing',
        joinDate: '2023-09-01',
        manager: 'Amit Sharma',
        officialEmail: 'sara.k@acmeco.com',
        // EMP-5678 does NOT require Office Keys
        currentAssets: ['Laptop (ACME-LPT-112)', 'ID Card', 'Desk Phone'],
        projectStatus: '100% Handover Complete (Signed off)',
        trainingStatus: 'Exit training pending on expense policy.',
        pendingLeaves: 5,
        employeeStatus: 'Active',
    },
};

const MOCK_ACTIVITY_LOGS = [
    { id: 1, employeeId: "EMP-1234", action: "Exit Process Initiated", modifiedBy: "HR Admin", date: "2024-01-15", details: "Employee exit process started - Resignation submitted" },
    { id: 2, employeeId: "EMP-5678", action: "Asset Return Completed", modifiedBy: "IT Admin", date: "2024-01-14", details: "All company assets returned and verified" },
    { id: 3, employeeId: "EMP-1234", action: "Clearance Document Generated", modifiedBy: "Manager", date: "2024-01-13", details: "Final clearance report downloaded and signed" },
    { id: 4, employeeId: "EMP-5678", action: "Final Settlement Processed", modifiedBy: "Finance Team", date: "2024-01-12", details: "Final salary and benefits settlement completed" },
    { id: 5, employeeId: "EMP-1234", action: "Access Revoked", modifiedBy: "System Admin", date: "2024-01-11", details: "All system access and credentials deactivated" },
];

const API_BASE_URL = 'http://localhost:8000';

const fetchEmployeeData = async (employeeId) => {
    try {
        const response = await adminOffboardingAPI.lookupEmployee(employeeId);
        console.log('RESPONSE:', response);
        
        // Response is already unwrapped by axios interceptor
        if (response && response.status === 'success') {
            return {
                name: response.employee_name,
                title: response.job_title,
                manager: response.reporting_manager,
                currentAssets: ['Laptop', 'Mobile', 'ID Card', 'Monitor'],
                employeeStatus: 'Active'
            };
        }
        return null;
    } catch (error) {
        console.error('Error fetching employee:', error);
        return null;
    }
};

const calculateExitDates = (resignationDate, resignationType, customNoticeDays = NOTICE_PERIOD_DAYS) => {
    if (!resignationDate) return { lastWorkingDay: 'N/A', daysToWork: 0, noticePeriodUsed: 0 };
    
    const resDate = new Date(resignationDate);
    let noticeDays = customNoticeDays;
    
    // Immediate Exit: LWD is the resignation date
    if (resignationType.includes('Immediate')) {
        noticeDays = 0;
    }
    
    const lwdDate = new Date(resDate);
    lwdDate.setDate(resDate.getDate() + noticeDays);
    
    // Format LWD
    const yyyy = lwdDate.getFullYear();
    const mm = String(lwdDate.getMonth() + 1).padStart(2, '0');
    const dd = String(lwdDate.getDate()).padStart(2, '0');
    const lastWorkingDay = `${yyyy}-${mm}-${dd}`;
    
    // Calculate remaining days from today until LWD
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    lwdDate.setHours(0, 0, 0, 0);

    const timeDiff = lwdDate.getTime() - today.getTime();
    const daysToWork = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24))); // Days remaining, minimum 0

    return { lastWorkingDay, daysToWork, noticePeriodUsed: noticeDays };
};

// --- Step Mapping for Exit Module (5 Phases) ---
const exitSteps = [
  { id: 1, name: 'Phase 1: Initiation & Exit Type', icon: MagnifyingGlassIcon, title: "Find Profile & Define Exit" },
  { id: 2, name: 'Phase 2: Last Working Day', icon: CalendarDaysIcon, title: "Calculate Notice Period" },
  { id: 3, name: 'Phase 3: Stakeholder Clearance', icon: ClipboardDocumentCheckIcon, title: "Verify Handover & Assets" },
  { id: 4, name: 'Phase 4: Manager/HR Declaration', icon: DocumentTextIcon, title: "Audit & Final Sign-Off" },
  { id: 5, name: 'Phase 5: Final Deactivation', icon: ArchiveBoxIcon, title: "Archive Profile & Block Access" },
];

// --- Sub-components (Reused) ---
const StepCard = ({ children, title, icon: Icon, isComplete, current }) => (
  <motion.div
    key={title}
    initial={{ x: 50, opacity: 0 }}
    animate={{ x: 0, opacity: 1 }}
    exit={{ x: -50, opacity: 0 }}
    transition={{ duration: 0.3 }}
    className={`bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 w-full`}
    style={{ fontFamily: 'Roboto, sans-serif' }}
  >
    <div className='flex items-center space-x-3 mb-6 border-b pb-4 border-gray-100'>
        <Icon className={`h-8 w-8 ${current ? `text-${PRIMARY_COLOR_CLASS}-600` : 'text-gray-400'}`} />
        <h2 className={`text-3xl font-normal text-gray-800`} style={{ fontFamily: 'Poppins, sans-serif' }}>{title}</h2>
        {isComplete && <CheckSolid className='h-6 w-6 text-green-500 ml-auto' />}
    </div>
    {children}
  </motion.div>
);

const InputGroup = ({ label, name, value, onChange, type, placeholder, required = true, readOnly = false, options = null, min = null }) => (
    <div>
        <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        {options ? (
             <select
                id={name}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                className={`mt-1 w-full px-4 py-2 rounded-lg text-gray-800 transition duration-150 bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-${PRIMARY_COLOR_CLASS}-500`}
            >
                {options.map(opt => (
                    <option key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</option>
                ))}
            </select>
        ) : (
            <input
                id={name}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                required={required}
                readOnly={readOnly}
                min={min}
                className={`mt-1 w-full px-4 py-2 rounded-lg text-gray-800 transition duration-150 ${
                    readOnly 
                    ? 'bg-gray-100 border border-gray-200 cursor-not-allowed'
                    : `bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-${PRIMARY_COLOR_CLASS}-500`
                }`}
            />
        )}
    </div>
);

const DataDisplay = ({ label, value, icon: Icon, color = 'gray' }) => (
    <div className={`p-4 rounded-xl shadow-md border-t-4 border-${color}-400 bg-white`}>
        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-1">
            <Icon className={`h-5 w-5 text-${color}-500`} />
            <span>{label}</span>
        </div>
        <p className="text-xl font-normal text-gray-800 break-all" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {value || 'N/A'}
        </p>
    </div>
);

// Activity Logs Component
const ActivityLogs = () => {
    return (
        <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-normal text-gray-800 mb-6 border-b pb-4 border-gray-100" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <ClockOutline className="h-8 w-8 inline-block mr-3 text-red-600"/>
                Activity Logs
            </h2>
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100/70">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modified By</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {MOCK_ACTIVITY_LOGS.map((log) => (
                            <tr key={log.id} className="hover:bg-red-50 transition duration-100">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.employeeId}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.action}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.modifiedBy}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.date}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{log.details}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Reports Component
const Reports = () => {
    const [reportType, setReportType] = useState('daily');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);

    const reportOptions = [
        { label: "Daily", value: "daily" },
        { label: "Monthly", value: "monthly" },
        { label: "Quarterly", value: "quarterly" },
        { label: "Half Yearly", value: "half-yearly" },
        { label: "Yearly", value: "yearly" },
        { label: "Custom Date Range", value: "custom" },
    ];

    const handleDownload = (format) => {
        setIsDownloading(true);
        setTimeout(() => {
            setIsDownloading(false);
            alert(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Exit Report downloaded in ${format.toUpperCase()} format!`);
        }, 2000);
    };

    return (
        <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-normal text-gray-800 mb-6 border-b pb-4 border-gray-100" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <DocumentArrowDownIcon className="h-8 w-8 inline-block mr-3 text-red-600"/>
                Exit Reports
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
                    <select
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white text-sm focus:ring-red-500 focus:border-red-500 transition duration-150"
                    >
                        {reportOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                
                {reportType === 'custom' && (
                    <>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                            <input
                                type="date"
                                value={customStartDate}
                                onChange={(e) => setCustomStartDate(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-sm focus:ring-red-500 focus:border-red-500 transition duration-150"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                            <input
                                type="date"
                                value={customEndDate}
                                onChange={(e) => setCustomEndDate(e.target.value)}
                                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-sm focus:ring-red-500 focus:border-red-500 transition duration-150"
                            />
                        </div>
                    </>
                )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
                <button
                    onClick={() => handleDownload('excel')}
                    disabled={isDownloading}
                    className={`px-6 py-3 text-sm font-semibold rounded-lg shadow-lg flex items-center justify-center bg-green-600 text-white hover:bg-green-700 focus:ring-4 focus:ring-green-300 transition duration-300 ${isDownloading ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                    {isDownloading ? (
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                        <TableCellsIcon className="h-5 w-5 mr-2" />
                    )}
                    {isDownloading ? 'Generating...' : 'Download Excel'}
                </button>
                
                <button
                    onClick={() => handleDownload('pdf')}
                    disabled={isDownloading}
                    className={`px-6 py-3 text-sm font-semibold rounded-lg shadow-lg flex items-center justify-center bg-red-600 text-white hover:bg-red-700 focus:ring-4 focus:ring-red-300 transition duration-300 ${isDownloading ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                    {isDownloading ? (
                        <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                        <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    )}
                    {isDownloading ? 'Generating...' : 'Download PDF'}
                </button>
            </div>
        </div>
    );
};

// --- Module Placeholder Components ---

const InterviewModule = () => (
    <div className='p-8 bg-white rounded-2xl shadow-2xl h-full flex flex-col justify-center items-center'>
        <UsersIcon className='h-24 w-24 text-gray-400 mb-4' />
        <h2 className='text-3xl font-medium text-gray-800'>1. Interview Process (To Be Built)</h2>
        <p className='text-gray-500 mt-2'>This module will handle Candidate Information, Rounds (Technical/Communication), and Performance Report Submission (Upload/Manual Entry).</p>
    </div>
);

const OfferLetterModule = () => (
    <div className='p-8 bg-white rounded-2xl shadow-2xl h-full flex flex-col justify-center items-center'>
        <DocumentTextIcon className='h-24 w-24 text-gray-400 mb-4' />
        <h2 className='text-3xl font-medium text-gray-800'>2. Offer Letter Process (To Be Built)</h2>
        <p className='text-gray-500 mt-2'>This module will manage HR input fields (CTC, Joining Date, Bond, T&C) and auto-generate the offer letter preview for sending.</p>
    </div>
);

const OnboardingModule = () => (
    <div className='p-8 bg-white rounded-2xl shadow-2xl h-full flex flex-col justify-center items-center'>
        <AcademicCapIcon className='h-24 w-24 text-gray-400 mb-4' />
        <h2 className='text-3xl font-medium text-gray-800'>3. Onboarding Process (To Be Built)</h2>
        <p className='text-gray-500 mt-2'>This module will focus on Document Upload (Resume/Offer Letter), system auto-extraction of key data, and final submission to the Employee Directory.</p>
    </div>
);

const ProfileModule = () => (
    <div className='p-8 bg-white rounded-2xl shadow-2xl h-full flex flex-col justify-center items-center'>
        <UserCircleIcon className='h-24 w-24 text-gray-400 mb-4' />
        <h2 className='text-3xl font-medium text-gray-800'>9. Employee Profile (To Be Built)</h2>
        <p className='text-gray-500 mt-2'>This module will display the comprehensive employee master data, including Personal, Contact, Official, Statutory, and Work History details as requested.</p>
    </div>
);

const LeaveAttendanceModule = () => (
    <div className='p-8 bg-white rounded-2xl shadow-2xl h-full flex flex-col justify-center items-center'>
        <CalendarDaysIcon className='h-24 w-24 text-gray-400 mb-4' />
        <h2 className='text-3xl font-medium text-gray-800'>5. Leave & Attendance (To Be Built)</h2>
        <p className='text-gray-500 mt-2'>This module manages leave requests, attendance corrections, and the approver module with audit trails.</p>
    </div>
);

const PoliciesModule = () => (
    <div className='p-8 bg-white rounded-2xl shadow-2xl h-full flex flex-col justify-center items-center'>
        <BookOpenIcon className='h-24 w-24 text-gray-400 mb-4' />
        <h2 className='text-3xl font-medium text-gray-800'>6. Policies Module (To Be Built)</h2>
        <p className='text-gray-500 mt-2'>This module allows HR to upload various company policies and manage employee acceptance/digital signing.</p>
    </div>
);

const AppraisalsModule = () => (
    <div className='p-8 bg-white rounded-2xl shadow-2xl h-full flex flex-col justify-center items-center'>
        <WalletIcon className='h-24 w-24 text-gray-400 mb-4' />
        <h2 className='text-3xl font-medium text-gray-800'>7. Appraisals / Approvals (To Be Built)</h2>
        <p className='text-gray-500 mt-2'>This module handles performance evaluation, promotions, salary hikes, and other HR-related approvals (role/dept changes).</p>
    </div>
);


// --- Exit Process Module (Refactored from V2) ---

const ExitProcessModule = ({ 
    exitData, setExitData, isLoading, setIsLoading, 
    lookupError, setLookupError, currentStep, setCurrentStep, 
    employeeIdInput, setEmployeeIdInput 
}) => {

    const employeeProfile = exitData.profile;
    const isStep1Valid = !!employeeProfile;
    const isStep2Valid = !!exitData.lastWorkingDay;

    // --- Handlers ---
    
    const handleIdChange = (e) => setEmployeeIdInput(e.target.value.toUpperCase());
    
    const handlePhase1Change = (e) => {
        const { name, value } = e.target;
        setExitData(prev => ({ 
            ...prev, 
            [name]: value,
            lastWorkingDay: '', 
            daysToWork: 0,
            // If Termination, reset notice days input
            customNoticeDays: name === 'exitType' && value === 'Termination' ? NOTICE_PERIOD_DAYS : prev.customNoticeDays
        }));
    };

    const handleChecklistChange = useCallback((key, value) => {
        // Handle high-level professional/financial checks
        if (['finalSettlementReady', 'projectHandover', 'trainingCompleted', 'pendingWorkCleared'].includes(key)) {
             setExitData(prev => ({
                ...prev,
                checklist: { ...prev.checklist, [key]: value },
            }));
        // Handle detailed asset status changes (new structure: {status: string, comments: string})
        } else if (exitData.checklist.detailedAssets.hasOwnProperty(key)) {
            setExitData(prev => ({
                ...prev,
                checklist: {
                    ...prev.checklist,
                    detailedAssets: {
                        ...prev.checklist.detailedAssets,
                        [key]: { ...prev.checklist.detailedAssets[key], ...value } // Merge updates
                    }
                }
            }));
        }
    }, [exitData.checklist.detailedAssets]);

    const handleAuditChange = useCallback((name, value) => {
        setExitData(prev => ({
            ...prev,
            audit: { ...prev.audit, [name]: value },
        }));
    }, []);

    // --- Derived State & Validation ---
    
    // Check if ALL checklist items (except detailedAssets) are true AND all detailed assets are returned (or partially/not returned with comment)
    const isStep3Valid = useMemo(() => {
        const highLevelChecks = ['finalSettlementReady', 'projectHandover', 'trainingCompleted', 'pendingWorkCleared'];
        const allHighLevelCleared = highLevelChecks.every(key => exitData.checklist[key] === true);
        
        const detailedAssets = exitData.checklist.detailedAssets;
        
        // Validation: All assets (that were fetched) must have a non-Pending status AND if status is Not/Partial, a comment is mandatory.
        // This handles the "Office Keys" fix: if the asset wasn't assigned (not in detailedAssets), it isn't checked.
        const allAssetsHandled = Object.entries(detailedAssets).length > 0 && Object.entries(detailedAssets).every(([assetName, asset]) => {
            if (asset.status === 'Pending') return false;
            // Comment is mandatory if status is not 'Returned'
            if (asset.status !== 'Returned' && asset.comments.trim().length === 0) return false;
            return true;
        });

        return allHighLevelCleared && allAssetsHandled;
    }, [exitData.checklist]);
    
    // Check if all Manager/HR/Admin declarations are complete and final status is selected.
    const isStep4Valid = useMemo(() => {
        const allDeclarationsComplete = exitData.audit.managerDeclaration && exitData.audit.hrDeclaration && exitData.audit.itDeclaration;
        const finalStatusSelected = ['Resigned', 'Terminated', 'Inactive'].includes(exitData.audit.finalEmployeeStatus);
        
        return allDeclarationsComplete && finalStatusSelected;
    }, [exitData.audit]);


    // Phase 1: Employee Lookup
    const handleEmployeeLookup = async () => {
        if (!employeeIdInput) {
            setLookupError("Please enter an Employee ID.");
            return;
        }

        setIsLoading(true);
        setLookupError('');
        const profile = await fetchEmployeeData(employeeIdInput);
        
        if (profile) {
            // Initializing asset structure with new details dynamically based on fetched profile assets
            const initialAssets = profile.currentAssets.reduce((acc, asset) => {
                acc[asset] = { status: 'Pending', comments: '' };
                return acc;
            }, {});

            setExitData(prev => ({ 
                ...prev, 
                profile, 
                employeeId: employeeIdInput,
                employeeStatus: profile.employeeStatus,
                checklist: { 
                    ...prev.checklist, 
                    detailedAssets: initialAssets,
                }
            }));
            setLookupError('');
            setCurrentStep(2); // Auto advance
        } else {
            setLookupError(`Employee ID ${employeeIdInput} not found. Please check the ID and try again.`);
            setExitData(prev => ({ ...prev, profile: null }));
        }
        setIsLoading(false);
    };

    // Phase 2: Automated Date Calculation
    const handleDateCalculation = () => {
        if (!exitData.resignationDate) return;
        
        const { lastWorkingDay, daysToWork, noticePeriodUsed } = calculateExitDates(
            exitData.resignationDate, 
            exitData.resignationType,
            exitData.customNoticeDays
        );

        setExitData(prev => ({ 
            ...prev, 
            lastWorkingDay, 
            daysToWork,
            noticePeriodUsed,
        }));
        setCurrentStep(3);
    };
    
    // Phase 4: Declaration/Report Download (simulated)
    const handleReportDownload = () => {
        if (isStep3Valid) {
            // Simulate generating and downloading a document
            const reportContent = `
                --- Clearance Report: ${employeeProfile.name} (ID: ${exitData.employeeId}) ---
                ... (Content omitted for brevity) ...
            `;
            console.log("Simulating Download of Clearance Report for:", employeeProfile.name);

            handleAuditChange('reportDownloaded', true);
            // In a real app: trigger file download via Blob
            window.alert("Clearance Report Download Simulated successfully.");
        }
    };


    // Phase 5: Final Deactivation
    const handleFinalDeactivation = async () => {
        setIsLoading(true);
        
        try {
            // Call backend API - this will update employee status and create notification
            console.log(`[API CALL]: Finalizing offboarding for ${exitData.employeeId}`);
            
            // Update local state
            setExitData(prev => ({
                ...prev,
                checklist: { ...prev.checklist, accessRevoked: true },
                employeeStatus: exitData.audit.finalEmployeeStatus
            }));
            
            console.warn(`[AUTOMATION]: System blocking all login credentials for ${exitData.employeeId}.`);
            console.log(`[AUTOMATION]: Employee Status updated to '${exitData.audit.finalEmployeeStatus}' from 'Active'.`);
            console.log("Employee Final Exit Payload:", exitData);
            
            setCurrentStep(6);
        } catch (error) {
            console.error('Error finalizing offboarding:', error);
            alert('Failed to finalize offboarding. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };


    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <StepCard title={exitSteps[0].title} icon={exitSteps[0].icon} current={true}>
                        <p className="text-gray-600 mb-6 text-sm">
                            Enter the Employee ID to retrieve the profile. Then, define the nature of the exit to set the offboarding track.
                        </p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                             <div className="col-span-1">
                                <InputGroup 
                                    label="Employee ID "
                                    name="employeeId" 
                                    value={employeeIdInput} 
                                    onChange={handleIdChange} 
                                    type="text" 
                                    placeholder="e.g., EMP-1234" 
                                />
                            </div>
                            <div className="col-span-1">
                                <InputGroup
                                    label="Exit Type"
                                    name="exitType"
                                    value={exitData.exitType}
                                    onChange={handlePhase1Change}
                                    options={[
                                        { label: 'Resignation (Employee)', value: 'Resignation' },
                                        { label: 'Termination (HR/Admin)', value: 'Termination' },
                                    ]}
                                />
                            </div>
                            <div className="col-span-1">
                                <InputGroup
                                    label="Notice Handling"
                                    name="resignationType"
                                    value={exitData.resignationType}
                                    onChange={handlePhase1Change}
                                    options={exitData.exitType === 'Resignation' ? [
                                        { label: 'Standard Notice Period', value: 'StandardNotice' },
                                        { label: 'Immediate Resignation', value: 'Immediate' },
                                    ] : [
                                        { label: 'Termination with Notice', value: 'TerminationNotice' },
                                        { label: 'Immediate Termination', value: 'TerminationImmediate' },
                                    ]}
                                />
                            </div>
                            {exitData.resignationType.includes('Notice') && (
                                <div className="col-span-1">
                                    <InputGroup 
                                        label="Notice Period (Days)"
                                        name="customNoticeDays" 
                                        value={exitData.customNoticeDays} 
                                        onChange={(e) => setExitData(prev => ({...prev, customNoticeDays: parseInt(e.target.value) || 0}))} 
                                        options={[
                                            { label: '15 Days', value: 15 },
                                            { label: '30 Days', value: 30 },
                                            { label: '60 Days', value: 60 },
                                            { label: '90 Days', value: 90 },
                                        ]}
                                    />
                                </div>
                            )}
                        </div>
                        
                        {exitData.resignationType.includes('Notice') && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <InputGroup
                                    label="Resignation Date"
                                    name="resignationDate"
                                    type="date"
                                    value={exitData.resignationDate}
                                    onChange={handlePhase1Change}
                                    required={true}
                                />
                                <InputGroup
                                    label="Last Working Day (Auto-calculated)"
                                    name="lastWorkingDay"
                                    type="date"
                                    value={exitData.lastWorkingDay}
                                    readOnly={true}
                                    required={false}
                                />
                            </div>
                        )}

                        <div className="flex gap-4">
                            <button
                                onClick={handleEmployeeLookup}
                                disabled={isLoading || !employeeIdInput}
                                className={`flex-1 px-6 py-3 rounded-lg bg-${PRIMARY_COLOR_CLASS}-600 text-white font-medium shadow-md hover:bg-${PRIMARY_COLOR_CLASS}-700 transition-colors disabled:opacity-50 flex items-center justify-center`}
                            >
                                <MagnifyingGlassIcon className='h-5 w-5 mr-2' />
                                {isLoading ? 'Searching...' : 'Lookup Employee'}
                            </button>
                            
                            {employeeProfile && exitData.resignationType.includes('Notice') && exitData.resignationDate && (
                                <button
                                    onClick={() => {
                                        const { lastWorkingDay, daysToWork, noticePeriodUsed } = calculateExitDates(
                                            exitData.resignationDate, 
                                            exitData.resignationType,
                                            exitData.customNoticeDays
                                        );
                                        setExitData(prev => ({ 
                                            ...prev, 
                                            lastWorkingDay, 
                                            daysToWork,
                                            noticePeriodUsed,
                                        }));
                                        window.alert('Resignation submitted successfully! Last working day calculated.');
                                    }}
                                    disabled={!exitData.resignationDate}
                                    className="flex-1 px-6 py-3 rounded-lg bg-green-600 text-white font-medium shadow-md hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                                >
                                    <DocumentTextIcon className='h-5 w-5 mr-2' />
                                    Submit Resignation
                                </button>
                            )}
                        </div>
                        
                        {lookupError && <p className="text-sm text-red-500 mt-2 flex items-center justify-center"><XSolid className='h-4 w-4 mr-1' /> {lookupError}</p>}
                        
                        {employeeProfile && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8 p-6 bg-green-50 rounded-xl border border-green-200">
                                <p className="text-lg font-medium text-green-700 mb-4 flex items-center">
                                    <CheckSolid className='h-5 w-5 mr-2' />
                                    Profile Loaded: {employeeProfile.name}
                                </p>
                                <div className='grid grid-cols-2 gap-4'>
                                    <DataDisplay label="Job Title" value={employeeProfile.title} icon={BriefcaseIcon} color='green' />
                                    <DataDisplay label="Reporting Manager" value={employeeProfile.manager} icon={UserMinusIcon} color='green' />
                                </div>
                            </motion.div>
                        )}
                    </StepCard>
                );

            case 2:
                return (
                    <StepCard title={exitSteps[1].title} icon={exitSteps[1].icon} current={true} isComplete={isStep2Valid}>
                        <p className="text-gray-600 mb-6 text-sm">
                            The system calculates the Last Working Day (LWD) based on the date and exit type provided. Click "Calculate LWD & Proceed" once the date is entered.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputGroup
                                label={exitData.exitType === 'Resignation' ? "Date of Resignation Submission" : "Date of Termination Notice"}
                                name="resignationDate"
                                type="date"
                                value={exitData.resignationDate}
                                onChange={handlePhase1Change}
                                required={true}
                            />
                             <InputGroup
                                label="Official Last Working Day (LWD)"
                                name="lastWorkingDay"
                                type="date"
                                value={exitData.lastWorkingDay}
                                readOnly={true}
                                required={false}
                            />
                        </div>

                        {isStep2Valid && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`mt-8 p-6 rounded-xl border-2 ${exitData.noticePeriodUsed > 0 ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'} text-center`}>
                                <p className="text-xl font-medium text-gray-700 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                    {exitData.noticePeriodUsed > 0 ? `Notice Period Applied: ${exitData.noticePeriodUsed} Days` : 'Immediate Exit: 0-Day Notice'}
                                </p>
                                <p className="text-3xl font-bold text-gray-800">
                                    {exitData.daysToWork > 0 ? `${exitData.daysToWork} Days Remaining Until LWD` : 'Exit Date Reached'}
                                </p>
                                <p className="text-sm text-gray-600 mt-2">
                                    Final LWD: **{new Date(exitData.lastWorkingDay).toDateString()}**
                                </p>
                            </motion.div>
                        )}
                    </StepCard>
                );

            case 3:
                return (
                    <StepCard title={exitSteps[2].title} icon={exitSteps[2].icon} current={true} isComplete={isStep3Valid}>
                        <p className="text-gray-600 mb-6 text-sm">
                            Verify the status of all critical offboarding tasks (Handover & Assets). **All items assigned to this employee must be handled to proceed.**
                        </p>
                        
                        <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
                            
                            {/* Professional/Handover Checks (Employee Submission - HR/Manager View) */}
                             <div className="p-4 bg-indigo-50 rounded-xl shadow-inner border border-indigo-200 col-span-1">
                                <p className="text-sm font-medium text-indigo-700 mb-3 flex items-center"><WrenchScrewdriverIcon className='h-5 w-5 mr-2 text-indigo-500' /> Professional & Handover</p>
                                <ul className='space-y-3'>
                                    {['projectHandover', 'trainingCompleted', 'pendingWorkCleared', 'finalSettlementReady'].map(key => (
                                        <li key={key} className='flex justify-between items-center text-sm text-gray-800'>
                                            <span>
                                                {key === 'projectHandover' ? 'Project Handover Completed' : 
                                                key === 'trainingCompleted' ? 'Mandatory Training Completed' : 
                                                key === 'pendingWorkCleared' ? 'No Pending Tasks' : 'Final Settlement Ready (HR/Finance)'}
                                            </span>
                                            <button
                                                onClick={() => handleChecklistChange(key, !exitData.checklist[key])}
                                                className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                                                    exitData.checklist[key] ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700 hover:bg-red-200'
                                                }`}
                                            >
                                                {exitData.checklist[key] ? 'Cleared' : 'Pending'}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            
                            {/* IT & Asset Return Tracking (HR/IT Verification) */}
                            <div className="p-4 bg-blue-50 rounded-xl shadow-inner border border-blue-200 md:col-span-2">
                                <p className="text-sm font-medium text-blue-700 mb-3 flex items-center"><ComputerDesktopIcon className='h-5 w-5 mr-2 text-blue-500' /> IT & Asset Return Status (Assigned: {Object.keys(exitData.checklist.detailedAssets).length})</p>
                                <ul className='space-y-4'>
                                    {Object.entries(exitData.checklist.detailedAssets).map(([assetName, assetData]) => (
                                        <li key={assetName} className='border-b last:border-b-0 pb-3'>
                                            <span className='font-semibold text-gray-900 block mb-2'>{assetName}</span>
                                            <div className='grid grid-cols-2 gap-4'>
                                                <InputGroup
                                                    label="Status"
                                                    name={`${assetName}-status`}
                                                    value={assetData.status}
                                                    onChange={(e) => handleChecklistChange(assetName, { status: e.target.value })}
                                                    options={[
                                                        { label: 'Pending Verification', value: 'Pending', disabled: true },
                                                        { label: 'Returned', value: 'Returned' },
                                                        { label: 'Partially Returned', value: 'Partially Returned' },
                                                        { label: 'Not Returned', value: 'Not Returned' },
                                                    ]}
                                                    required={true}
                                                />
                                                <textarea
                                                    name={`${assetName}-comments`}
                                                    rows="1"
                                                    value={assetData.comments}
                                                    onChange={(e) => handleChecklistChange(assetName, { comments: e.target.value })}
                                                    placeholder={`Mandatory comment if ${assetData.status === 'Partially Returned' || assetData.status === 'Not Returned' ? 'required' : 'optional'}`}
                                                    className={`w-full px-3 py-2 rounded-lg bg-white border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm mt-5 ${
                                                        (assetData.status === 'Partially Returned' || assetData.status === 'Not Returned') && assetData.comments.trim().length === 0 ? 'border-red-500' : ''
                                                    }`}
                                                />
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        
                        {isStep3Valid && (
                            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 p-4 bg-green-100 rounded-xl text-green-800 text-center font-medium">
                                All Stakeholder Checklists Complete! Ready for Manager/HR Declaration.
                            </motion.div>
                        )}
                        {!isStep3Valid && (
                             <div className="mt-6 p-4 bg-yellow-100 rounded-xl text-yellow-800 text-center font-medium text-sm">
                                ⚠️ Please ensure all Handover items are cleared and all **{Object.keys(exitData.checklist.detailedAssets).length} Assigned Assets** have a status (not Pending) and required comments.
                            </div>
                        )}
                    </StepCard>
                );

            case 4:
                return (
                    <StepCard title={exitSteps[3].title} icon={exitSteps[3].icon} current={true} isComplete={isStep4Valid}>
                        <p className="text-gray-600 mb-6 text-sm">
                            Final audit and sign-off by relevant officials. This confirms all regulatory and internal clearance is complete.
                        </p>
                        <div className="space-y-6">

                             {/* Declaration/Report Download */}
                            <div className="p-6 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 text-center">
                                <p className="text-lg font-medium text-blue-800 mb-3">Download Official Clearance Document</p>
                                <button
                                    onClick={handleReportDownload}
                                    disabled={!isStep3Valid || exitData.audit.reportDownloaded}
                                    className={`px-6 py-2 rounded-full font-semibold transition-colors flex items-center justify-center mx-auto ${
                                        exitData.audit.reportDownloaded
                                        ? 'bg-blue-200 text-blue-800 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                                    }`}
                                >
                                    <ArrowDownTrayIcon className='h-5 w-5 mr-2' />
                                    {exitData.audit.reportDownloaded ? 'Clearance Report Downloaded (Done)' : 'Download & Sign Clearance Report'}
                                </button>
                                {!isStep3Valid && <p className='text-xs text-red-500 mt-2'>*Must complete Phase 3 checklist first.</p>}
                            </div>

                            <p className="text-xl font-medium text-gray-800 border-b pb-2">Stakeholder Declarations</p>
                            
                            {/* Manager/HR Declarations (Combined) */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[{key: 'managerDeclaration', label: 'Manager Declaration (Handover)', icon: UserMinusIcon},
                                  {key: 'hrDeclaration', label: 'HR Declaration (PF/Settlement)', icon: BuildingOfficeIcon},
                                  {key: 'itDeclaration', label: 'IT/Admin Declaration (Asset/Access)', icon: ComputerDesktopIcon},
                                ].map(({key, label, icon: Icon}) => (
                                    <div key={key} className="p-4 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                                        <label className="text-sm font-medium text-gray-700 flex items-center"><Icon className='h-5 w-5 mr-2 text-red-600' />{label}</label>
                                        <button
                                            onClick={() => handleAuditChange(key, !exitData.audit[key])}
                                            disabled={!exitData.audit.reportDownloaded}
                                            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                                                exitData.audit[key] ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                            } disabled:opacity-50`}
                                        >
                                            {exitData.audit[key] ? 'Approved ✔' : 'Pending'}
                                        </button>
                                    </div>
                                ))}
                            </div>
                            
                            {/* Final Status Dropdown */}
                            <div className="p-6 rounded-xl border-2 border-dashed border-red-300 bg-red-50 text-center">
                                <p className="text-lg font-medium text-red-800 mb-3">Final Employee Status Update</p>
                                <InputGroup
                                    label="Select Final Status"
                                    name="finalEmployeeStatus"
                                    value={exitData.audit.finalEmployeeStatus}
                                    onChange={(e) => handleAuditChange('finalEmployeeStatus', e.target.value)}
                                    options={[
                                        { label: '-- Select Status --', value: '', disabled: true },
                                        { label: 'Resigned', value: 'Resigned' },
                                        { label: 'Terminated', value: 'Terminated' },
                                        { label: 'Inactive', value: 'Inactive' },
                                    ]}
                                />
                                {!isStep4Valid && <p className='text-xs text-red-500 mt-2'>*All declarations and status selection are required to proceed.</p>}
                            </div>
                        </div>
                    </StepCard>
                );

            case 5:
                return (
                    <StepCard title={exitSteps[4].title} icon={exitSteps[4].icon} current={true}>
                         <p className="text-gray-600 mb-8 text-sm">
                            This is the final, irreversible step. The Admin confirms the termination. This action will automatically **block all login credentials** and update the employee's status in the system.
                        </p>
                        
                        <div className="p-6 bg-red-100 border border-red-300 rounded-xl space-y-4">
                            <DataDisplay label="Employee Name" value={employeeProfile?.name} icon={UserCircleIcon} color='red' />
                            <DataDisplay label="LWD" value={exitData.lastWorkingDay} icon={CalendarDaysIcon} color='red' />
                            <DataDisplay label="New Final Status" value={exitData.audit.finalEmployeeStatus} icon={ClockIcon} color='red' />
                        </div>
                        
                        <div className="mt-8 p-4 bg-yellow-50 rounded-lg text-sm font-medium text-yellow-800 border border-yellow-200">
                             ⚠️ **Warning:** Clicking the button below confirms all steps. The employee status will be set to **{exitData.audit.finalEmployeeStatus}**.
                        </div>
                        
                    </StepCard>
                );

            default:
                return null;
        }
    };
    
    // Consolidated Next Step handler with validation
    const handleNext = () => {
        if (currentStep === 1) {
            if (isStep1Valid) setCurrentStep(2);
        } else if (currentStep === 2) {
            handleDateCalculation();
        } else if (currentStep === 3) {
            if (isStep3Valid) setCurrentStep(4);
        } else if (currentStep === 4) {
            if (isStep4Valid) setCurrentStep(5);
        }
    };
    
    // Function to render the right side content (exit process steps/cards)
    const renderContent = () => {
        if (currentStep > 5) {
            // Final success screen
            return (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white p-16 rounded-3xl shadow-2xl text-center border border-green-200 min-h-[500px] flex flex-col justify-center"
                >
                    <ArchiveBoxIcon className="h-28 w-28 text-green-500 mx-auto mb-8 animate-pulse" />
                    <h2 className="text-4xl font-normal text-green-700 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>{exitData.audit.finalEmployeeStatus} Process Complete!</h2>
                    <p className="text-xl text-gray-600 font-normal">
                        **{employeeProfile?.name || 'Employee'} (ID: {exitData.employeeId})** status has been successfully updated.
                    </p>
                    <p className={`text-lg text-${PRIMARY_COLOR_CLASS}-600 mt-2 font-medium`}>
                        Final Status: **{exitData.employeeStatus}**.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className={`mt-8 px-6 py-2 rounded-full bg-${PRIMARY_COLOR_CLASS}-600 text-white font-medium shadow-lg hover:bg-${PRIMARY_COLOR_CLASS}-700 transition-colors text-base`}
                    >
                        Start New Exit Process
                    </button>
                </motion.div>
            );
        } else {
            // Render the current step card and navigation buttons
            return (
                <>
                    <AnimatePresence mode="wait">
                        {renderCurrentStep()}
                    </AnimatePresence>
                    
                    {/* Navigation Buttons */}
                    <div className={`flex justify-between items-center mt-8 p-0 pt-5`}>
                        {currentStep > 1 && (
                            <button
                                onClick={() => setCurrentStep(currentStep - 1)}
                                disabled={isLoading}
                                className="px-6 py-2 rounded-full text-gray-700 font-medium bg-gray-100 border border-gray-300 hover:bg-gray-200 transition-colors disabled:opacity-50 shadow-md text-base"
                            >
                                &larr; Back
                            </button>
                        )}
                        <div className={currentStep === 1 ? "ml-auto" : ""}>
                            {currentStep < 5 ? (
                                <button
                                    onClick={handleNext}
                                    disabled={isLoading || (currentStep === 1 && !isStep1Valid) || (currentStep === 3 && !isStep3Valid) || (currentStep === 4 && !isStep4Valid)}
                                    className={`px-6 py-2 rounded-full bg-${PRIMARY_COLOR_CLASS}-600 text-white font-medium shadow-xl shadow-${PRIMARY_COLOR_CLASS}-300 hover:bg-${PRIMARY_COLOR_CLASS}-700 transition-colors disabled:opacity-50 text-base flex items-center`}
                                >
                                    {isLoading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            {currentStep === 2 ? 'Calculate LWD & Proceed' : currentStep === 4 ? 'Confirm & Finalize' : 'Next Step'} &rarr;
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button
                                    onClick={handleFinalDeactivation}
                                    disabled={isLoading}
                                    className="px-6 py-2 rounded-full bg-red-600 text-white font-medium shadow-xl shadow-red-300 hover:bg-red-700 transition-colors disabled:opacity-50 text-base flex items-center"
                                >
                                    {isLoading ? (
                                         <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                            Finalizing Termination...
                                        </>
                                    ) : (
                                        `EXECUTE Final ${exitData.audit.finalEmployeeStatus}`
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )
        }
    }

    return renderContent();
}


// --- Main HRMS Dashboard Component ---

const HRMSDashboard = () => {
    // --- Global State for the Exit Process Module ---
    const [employeeIdInput, setEmployeeIdInput] = useState(DEFAULT_TEST_ID);
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [lookupError, setLookupError] = useState('');
    
    // State to manage the currently viewed module
    const [selectedModule, setSelectedModule] = useState('Exit');
    const [activeTab, setActiveTab] = useState('exit-process'); 

    // Initialize exitData state
    const [exitData, setExitData] = useState({
        employeeId: '',
        profile: null,
        exitType: 'Resignation', 
        resignationType: 'StandardNotice',
        resignationDate: new Date().toISOString().split('T')[0],
        customNoticeDays: NOTICE_PERIOD_DAYS, 
        lastWorkingDay: '',
        daysToWork: 0,
        noticePeriodUsed: 0,

        // Phase 3: Checklist Status
        checklist: {
            projectHandover: false,
            trainingCompleted: false,
            pendingWorkCleared: false,
            finalSettlementReady: false,
            detailedAssets: {}, 
        },
        
        // Phase 4: Feedback & Audit
        audit: {
            managerDeclaration: false, 
            hrDeclaration: false,      
            itDeclaration: false,      
            notes: '',
            reportDownloaded: false,
            finalEmployeeStatus: '',
        },
        
        employeeStatus: 'Active', 
    });


    // Auto load profile if default ID is set (Only happens once on initial load for Exit module)
    React.useEffect(() => {
        if (selectedModule === 'Exit' && !exitData.profile && employeeIdInput === DEFAULT_TEST_ID) {
            fetchEmployeeData(DEFAULT_TEST_ID).then(profile => {
                if (profile) {
                    const initialAssets = profile.currentAssets.reduce((acc, asset) => {
                        acc[asset] = { status: 'Pending', comments: '' };
                        return acc;
                    }, {});

                    setExitData(prev => ({ 
                        ...prev, 
                        profile, 
                        employeeId: DEFAULT_TEST_ID,
                        employeeStatus: profile.employeeStatus,
                        checklist: { 
                            ...prev.checklist, 
                            detailedAssets: initialAssets,
                        }
                    }));
                }
            });
        }
    }, [selectedModule]); 

    
    const renderModule = () => {
        switch (selectedModule) {
            case 'Exit':
                // Tab Navigation for Exit Module
                if (activeTab === 'exit-process') {
                    return <ExitProcessModule 
                                exitData={exitData}
                                setExitData={setExitData}
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                                lookupError={lookupError}
                                setLookupError={setLookupError}
                                currentStep={currentStep}
                                setCurrentStep={setCurrentStep}
                                employeeIdInput={employeeIdInput}
                                setEmployeeIdInput={setEmployeeIdInput}
                            />;
                } else if (activeTab === 'activity-logs') {
                    return <ActivityLogs />;
                } else if (activeTab === 'reports') {
                    return <Reports />;
                }
                break;
            case 'Interview':
                return <InterviewModule />;
            case 'OfferLetter':
                return <OfferLetterModule />;
            case 'Onboarding':
                return <OnboardingModule />;
            case 'Profile':
                return <ProfileModule />;
            case 'LeaveAttendance':
                return <LeaveAttendanceModule />;
            case 'Policies':
                return <PoliciesModule />;
            case 'Appraisals':
                return <AppraisalsModule />;
            default:
                // Default to Exit process
                return <ExitProcessModule {...{exitData, setExitData, isLoading, setIsLoading, lookupError, setLookupError, currentStep, setCurrentStep, employeeIdInput, setEmployeeIdInput}} />;
        }
    };
    
    const moduleList = [
        { key: 'Interview', name: '1. Interview Process', icon: UsersIcon },
        { key: 'OfferLetter', name: '2. Offer Letter Process', icon: DocumentTextIcon },
        { key: 'Onboarding', name: '3. Onboarding Process', icon: AcademicCapIcon },
        { key: 'Exit', name: '4. Exit Process', icon: BriefcaseIcon },
        { key: 'LeaveAttendance', name: '5. Leave & Attendance', icon: CalendarDaysIcon },
        { key: 'Policies', name: '6. Policies Module', icon: BookOpenIcon },
        { key: 'Appraisals', name: '7. Appraisals / Approvals', icon: WalletIcon },
        { key: 'Profile', name: '9. Employee Profile', icon: UserCircleIcon },
    ];
    
    // Logic for rendering the correct sidebar navigation
    const renderSidebarNavigation = () => {
        if (selectedModule === 'Exit') {
            // Show tabs for Exit module
            return (
                <div className="space-y-2">
                    <button
                        onClick={() => setActiveTab('exit-process')}
                        className={`w-full text-left p-3 rounded-lg flex items-center transition-colors border ${
                            activeTab === 'exit-process'
                            ? `bg-${PRIMARY_COLOR_CLASS}-50 text-${PRIMARY_COLOR_CLASS}-700 font-semibold border-${PRIMARY_COLOR_CLASS}-300 shadow-md`
                            : 'text-gray-600 hover:bg-gray-100 border-transparent'
                        } text-sm`}
                    >
                        <BriefcaseIcon className='h-5 w-5 mr-2' />
                        Exit Process
                    </button>
                    <button
                        onClick={() => setActiveTab('activity-logs')}
                        className={`w-full text-left p-3 rounded-lg flex items-center transition-colors border ${
                            activeTab === 'activity-logs'
                            ? `bg-${PRIMARY_COLOR_CLASS}-50 text-${PRIMARY_COLOR_CLASS}-700 font-semibold border-${PRIMARY_COLOR_CLASS}-300 shadow-md`
                            : 'text-gray-600 hover:bg-gray-100 border-transparent'
                        } text-sm`}
                    >
                        <ClockOutline className='h-5 w-5 mr-2' />
                        Activity Logs
                    </button>
                    <button
                        onClick={() => setActiveTab('reports')}
                        className={`w-full text-left p-3 rounded-lg flex items-center transition-colors border ${
                            activeTab === 'reports'
                            ? `bg-${PRIMARY_COLOR_CLASS}-50 text-${PRIMARY_COLOR_CLASS}-700 font-semibold border-${PRIMARY_COLOR_CLASS}-300 shadow-md`
                            : 'text-gray-600 hover:bg-gray-100 border-transparent'
                        } text-sm`}
                    >
                        <DocumentArrowDownIcon className='h-5 w-5 mr-2' />
                        Reports
                    </button>
                    
                    {/* Show exit steps only when on exit-process tab */}
                    {activeTab === 'exit-process' && (
                        <>
                            <div className="border-t border-gray-200 my-4"></div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Process Steps</p>
                            {exitSteps.map((step) => (
                                <button
                                    key={step.id}
                                    onClick={() => setCurrentStep(step.id)}
                                    className={`w-full text-left p-2 rounded-lg flex items-center transition-colors border ml-4 ${
                                        currentStep === step.id 
                                        ? `bg-${PRIMARY_COLOR_CLASS}-50 text-${PRIMARY_COLOR_CLASS}-700 font-semibold border-${PRIMARY_COLOR_CLASS}-300 shadow-md`
                                        : (currentStep > step.id ? 'text-gray-600 hover:bg-gray-100 border-transparent' : 'text-gray-400 hover:bg-gray-50 border-transparent')
                                    } text-xs`}
                                >
                                    {currentStep > step.id ? <CheckSolid className='h-4 w-4 mr-2 text-green-500' /> : <ArrowLongRightIcon className='h-4 w-4 mr-2 text-gray-400' />}
                                    <span className='mr-1 font-medium'>Phase {step.id}:</span>
                                    {step.name.split(': ')[1]}
                                </button>
                            ))}
                        </>
                    )}
                </div>
            );
        } else {
            return moduleList.map((module) => (
                <button
                    key={module.key}
                    onClick={() => {
                        setSelectedModule(module.key);
                        // Reset Exit process steps if navigating away and back
                        if (module.key === 'Exit') {
                            setCurrentStep(1);
                            setActiveTab('exit-process');
                        }
                    }}
                    className={`w-full text-left p-3 rounded-lg flex items-center transition-colors ${
                        selectedModule === module.key 
                        ? `bg-${PRIMARY_COLOR_CLASS}-50 text-${PRIMARY_COLOR_CLASS}-700 font-semibold border border-${PRIMARY_COLOR_CLASS}-300 shadow-md`
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                    <module.icon className={`h-5 w-5 mr-3 ${selectedModule === module.key ? `text-${PRIMARY_COLOR_CLASS}-600` : 'text-gray-500'}`} />
                    {module.name}
                </button>
            ));
        }
    };


    return (
        <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Roboto, sans-serif' }}>
            <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                <header className="mb-10 p-6 bg-white border-b border-gray-100 shadow-lg rounded-2xl">
                    <h1 className="text-4xl font-normal text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
                         Employee Offboarding Process
                    </h1>
                    <p className={`text-xl font-normal text-${PRIMARY_COLOR_CLASS}-600 mt-1`} style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {/* Current Module: **{moduleList.find(m => m.key === selectedModule)?.name || selectedModule}** */}
                    </p>
                </header>

                <div className="flex flex-col lg:flex-row gap-10">
                    {/* Module Selection Sidebar */}
                    <div className="lg:w-1/4">
                        <div className={`sticky top-4 p-6 bg-white rounded-2xl shadow-xl border border-gray-200`}>
                            <h3 className="text-xl font-normal text-gray-800 mb-5 flex items-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                <HomeModernIcon className='h-6 w-6 mr-2 text-gray-500' />
                                {selectedModule === 'Exit' ? 'Exit Module' : 'System Modules'}
                            </h3>
                            <div className="space-y-2">
                                {renderSidebarNavigation()}
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="lg:w-3/4">
                        {renderModule()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HRMSDashboard;